export type EquipmentStatus =
  | 'available'
  | 'reserved'
  | 'checked-out'
  | 'maintenance'
  | 'retired'
  | 'lost'

export interface EquipmentTypeCounts {
  available: number
  reserved: number
  checkedOut: number
  maintenance: number
  retired: number
  lost: number
  total: number
}

export type EquipmentTypeStatus = 'draft' | 'active' | 'archived'

export interface EquipmentProduct {
  id: number
  code: string
  name: string
  description: string | null
  isActive: boolean
}

export interface EquipmentProductCreateInput {
  code: string
  name: string
  description?: string | null
  isActive?: boolean
}

export interface EquipmentProductUpdateInput {
  code?: string
  name?: string
  description?: string | null
  isActive?: boolean
}

export interface EquipmentTypeSummary {
  id: number
  key: string
  name: string
  make: string
  model: string
  descriptor: string | null
  categoryId: number | null
  categoryName: string | null
  isAccessory: boolean
  status: EquipmentTypeStatus
  products: string[]
  specs?: EquipmentTypeSpecs | null
  assets?: EquipmentTypeAsset[]
  accessories?: EquipmentTypeAccessory[]
  counts: EquipmentTypeCounts
}

export interface EquipmentTypeSpecs {
  shippingWeightOz: number | null
  shippingHeightIn: number | null
  shippingWidthIn: number | null
  shippingDepthIn: number | null
  metadata?: Record<string, unknown>
}

export type EquipmentAssetType = 'front_image' | 'back_image' | 'datasheet' | 'other'

export interface EquipmentTypeAsset {
  id?: number
  equipmentTypeId?: number
  assetType: EquipmentAssetType
  filePath: string | null
  displayOrder?: number
  metadata?: Record<string, unknown>
  localFile?: File
  previewUrl?: string | null
  isPending?: boolean
  tempId?: string
}

export interface EquipmentTypeAccessory {
  equipmentTypeId?: number
  accessoryForTypeId: number | null
  notes?: string | null
}

export interface EquipmentSerial {
  id: number
  equipmentTypeId: number
  serial: string | null
  status: EquipmentStatus
  condition: string | null
  notes: string | null
  lastStatusChange: string | null
  createdAt: string
  updatedAt: string
}

export const EQUIPMENT_STATUSES: EquipmentStatus[] = [
  'available',
  'reserved',
  'checked-out',
  'maintenance',
  'retired',
  'lost',
]

export type EquipmentTypeUpdateInput = {
  name?: string
  make?: string
  model?: string
  descriptor?: string | null
  isAccessory?: boolean
  categoryId?: number | null
  status?: EquipmentTypeStatus
  products?: string[]
}

export interface EquipmentTypeCreateInput {
  name: string
  make: string
  model: string
  descriptor?: string | null
  categoryId?: number | null
  isAccessory?: boolean
  status?: EquipmentTypeStatus
  products?: string[]
  specs?: EquipmentTypeSpecs
  assets?: EquipmentTypeAsset[]
  accessories?: EquipmentTypeAccessory[]
}

export interface EquipmentTypeDraft {
  id: number
  name: string | null
  make: string | null
  model: string | null
  descriptor: string | null
  category_id: number | null
  is_accessory: boolean
  products: string[] | null
  specs: EquipmentTypeSpecs | null
  assets: EquipmentTypeAsset[] | null
  accessories: EquipmentTypeAccessory[] | null
  progress_step: string | null
  created_at: string
  updated_at: string
}

export interface EquipmentTypeDraftInput {
  name?: string
  make?: string
  model?: string
  descriptor?: string | null
  categoryId?: number | null
  isAccessory?: boolean
  products?: string[]
  specs?: EquipmentTypeSpecs
  assets?: EquipmentTypeAsset[]
  accessories?: EquipmentTypeAccessory[]
  progressStep?: string
}

export interface EquipmentSerialCreateInput {
  equipmentTypeId: number
  serial?: string | null
  barcode?: string | null
  status: EquipmentStatus
  condition?: string | null
  notes?: string | null
}

export type EquipmentSerialStatusChange = {
  fromStatus: EquipmentStatus
  toStatus: EquipmentStatus
  context?: Record<string, unknown>
}
